import { useState, useEffect } from "react";
import './ProjectsPage.css';
import {
    Button,
    Container,
    styled,
    Box,
    CircularProgress,
} from "@mui/material";
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import FigmaModal from "components/modal/FigmaModal";
import { useLocation } from 'react-router-dom';
import UploadButton from "components/button/UploadButton";
import { useGetComplianceExportQuery, useGetProjectsExportQuery, useGetUserQuery, useLazyGetComplianceExportQuery, useLazyGetProjectsExportQuery } from "api/api";
import StyledButton from "components/button/StyledButton";
import { useFormContext } from "context/FormContext";
import ProjectsTable from "components/tables/ProjectTable";
import MessageModal from "components/modal/MessageModal";
// import importTemplate from "assets/Project_Import_Template.xlsm"

type ModalType = 'addFirm' | 'addFirmSuccess' | 'addProject' | 'updateCarbonFactor';


const ProjectsPage = () => {
    const { setRunningTotal, setSubstructureTotal, setSuperstructureTotal, setSuperstructureContTotal } = useFormContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState<ModalType>('addProject');
    const [showImportAcknowledgement, setShowImportAcknowledgement] = useState<boolean>(false);
    const location = useLocation();
    const { data: userData } = useGetUserQuery();
    const [triggerProjectExport, {
        data: projectExportBlob,
        isLoading: isProjectLoading,
        isSuccess: isProjectSuccess
    }] = useLazyGetProjectsExportQuery();

    const [triggerComplianceExport, {
        data: complianceExportBlob,
        isLoading: isComplianceLoading,
        isSuccess: isComplianceSuccess
    }] = useLazyGetComplianceExportQuery();

    const isProjectExportAllowed = userData?.site_role !== "Firm User";
    const isComplianceExportAllowed = ["Sub Super Admin", "Super Admin"].includes(userData?.site_role ?? "");

    const handleExport = async (type: 'project' | 'compliance') => {
        const config = {
            project: {
                isAllowed: isProjectExportAllowed,
                trigger: triggerProjectExport,
                blob: projectExportBlob,
                filename: 'projects-export.xlsx'
            },
            compliance: {
                isAllowed: isComplianceExportAllowed,
                trigger: triggerComplianceExport,
                blob: complianceExportBlob,
                filename: 'compliance-export.xlsx'
            }
        };

        const exportConfig = config[type];

        if (!exportConfig.isAllowed) return;

        try {
            // Triggers the export and get the blob response
            const blobResponse = await exportConfig.trigger(null).unwrap();
            if (!blobResponse) return;

            // Creates a download link directly from the blob
            const url = window.URL.createObjectURL(blobResponse);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", exportConfig.filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(`Error exporting ${type} data:`, error);
        }
    };

    const handleSuccessProjectImport = () => {
        setShowImportAcknowledgement(true);
    }

    // Modal functions
    // Create New Project onClick()
    const handleOpenModal = () => {
        setRunningTotal("0");
        setSubstructureTotal("0");
        setSuperstructureTotal("0");
        setSuperstructureContTotal("0");
        setIsModalOpen(true);
    }

    const handleCloseModal = () => {
        setIsModalOpen(false);
        if (modalType !== 'addProject') {
            setModalType('addProject' as ModalType);
        }
    }

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const modal = queryParams.get('modal');
        const modalTypeParam = queryParams.get('modalType');

        if (modal === 'open' && modalTypeParam) {
            setIsModalOpen(true);
            if (modalTypeParam === 'addProjectSuccess' || modalTypeParam === 'updateProjectSuccess') {
                setModalType(modalTypeParam as ModalType);
            }
        }
    }, [location]);

    if (userData?.site_role === "Sub Super Admin") {
        return (
            <StyledContainer>
                <div style={{ maxWidth: '90vw', width: '84vw', height: '100%', minHeight: '8vh', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 20, paddingTop: 1, marginTop: 20 }}>
                    <Box sx={{
                        display: 'flex',
                        mt: '2vh',
                    }}>
                        {isProjectExportAllowed && <StyledButton
                            type="filled"
                            label="Export Preaggregated Data"
                            onClick={() => handleExport('project')}
                            disabled={isProjectLoading}
                            endAdornment={isProjectLoading && (
                                <CircularProgress
                                    size={16}
                                    sx={{ color: '#fff' }}
                                />
                            )}
                        />}
                        {isComplianceExportAllowed && <StyledButton
                            type="filled"
                            label="Export Compliance Data"
                            onClick={() => handleExport('compliance')}
                            disabled={isComplianceLoading}
                            endAdornment={isComplianceLoading && (
                                <CircularProgress
                                    size={16}
                                    sx={{ color: '#fff' }}
                                />
                            )}
                        />}
                    </Box>
                </div>
                <MessageModal
                    open={showImportAcknowledgement}
                    onClose={() => setShowImportAcknowledgement(false)}
                    message={showImportAcknowledgement ? `Firms have been successfully imported.` : null}
                    buttonText="Ok"
                />
            </StyledContainer>
        );
    }

    return (
        <StyledContainer>
            <FigmaModal open={isModalOpen} onClose={handleCloseModal} type={modalType} />
            <Box sx={{
                display: 'flex',
            }}>
                <Button
                    type="button"
                    onClick={handleOpenModal}
                    variant="outlined"
                    sx={{ mr: 2, borderRadius: 12 }}
                    startIcon={<AddIcon />}
                >
                    Add Project
                </Button>
                <UploadButton type="projects" onSuccess={handleSuccessProjectImport}></UploadButton>
                <Button
                    component="a"
                    href="/assets/SE-2050-Project-Import_Spreadsheet_v1.2.xlsx"
                    download
                    variant="outlined"
                    sx={{ mr: 2, borderRadius: 12 }}
                    startIcon={<ArticleIcon />}
                >
                    Download Import Template
                </Button>
                {isProjectExportAllowed && <Button
                    onClick={() => handleExport('project')}
                    variant="outlined"
                    sx={{ mr: 2, borderRadius: 12 }}
                    startIcon={isProjectLoading ? (
                        <CircularProgress size={16} />
                    ) : (
                        <img src={'/assets/icons/ExportIcon.svg'} alt='Export Icon' />
                    )}
                >
                    Export Projects
                </Button>}
            </Box>
            <div style={{ maxWidth: '84vw', width: '100%', height: '100%', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 20, paddingTop: 1, marginTop: 20 }}>
                <ProjectsTable isSuperAdmin={userData?.site_role === 'Super Admin'} />
            </div>
            <MessageModal
                open={showImportAcknowledgement}
                onClose={() => setShowImportAcknowledgement(false)}
                message={showImportAcknowledgement ? `Projects have been successfully imported.` : null}
                buttonText="Ok"
            />
        </StyledContainer>
    );
};

export default ProjectsPage;

const StyledContainer = styled(Container)({
    marginBottom: 24,
});